import type { ReactNode } from 'react';
import { cn } from '../../../../utils';
import {
  renderInlineMarkdown,
  renderPlainTextWithLinks,
  renderMarkdownFromText,
} from '../../../custom/markdown';
import { useLessonLinkClasses } from './lesson-link-classes';

interface ContentDescriptionProps {
  text?: string | null;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  markdown?: boolean;
}

const markdownBlockClasses = {
  paragraph: 'm-0 text-sm leading-relaxed text-muted-foreground',
  heading: 'm-0 text-xl font-bold text-foreground',
  blockquote:
    'm-0 border-l-2 border-slate-300/70 bg-slate-100/40 px-3 py-2 rounded text-foreground',
  blockquoteLine: 'm-0',
  blockquoteGap: 'h-3',
  text: undefined, // Don't override text color in formatted tokens
  color: undefined, // Let color tokens apply their own color
  link: undefined,
  strong: undefined,
  italic: undefined,
  underline: undefined,
};

export function ContentDescription({
  text,
  className,
  as,
  markdown = false,
}: ContentDescriptionProps) {
  const linkClasses = useLessonLinkClasses();

  if (!text || text.trim().length === 0) {
    return null;
  }

  const Component = markdown ? (as ?? 'div') : (as ?? 'p');

  // When rendering markdown blocks, don't apply base text styles to container
  // as each block element handles its own styling
  const containerClasses = markdown
    ? cn('space-y-2', className)
    : cn('whitespace-pre-line text-sm leading-relaxed text-muted-foreground', className);

  return (
    <Component className={containerClasses}>
      {markdown
        ? renderMarkdownFromText(text, 'content-description', {
            ...markdownBlockClasses,
            ...linkClasses,
          })
        : renderInlineMarkdown(text, 'content-description', linkClasses)}
    </Component>
  );
}

export function renderContentText(text?: string | null, keyPrefix = 'content-text'): ReactNode {
  return renderPlainTextWithLinks(text, keyPrefix);
}

